// import external libraries
import dayjs from 'dayjs';

// import constants
import {
  CARE_RECEIVERS_SESSION_STORAGE_KEYS,
  TabType
} from 'components/roster-portal/care-receivers/types';
import { MRP_SESSION_STORAGE_KEYS } from 'components/mrp/mrp.consts';
import { BACKLOG_CATEGORIES } from './Backlog.consts';

// import types
import type { CalendarSourceData } from 'components/mrp/stores/calendarStore/calendarStore.types';
import type { CareReceiverHandover } from 'components/roster-portal/care-receivers/types';
import type {
  BacklogCategoryFilter,
  PlanningEntityType
} from 'generated/index';
import type {
  BacklogEntityFilter,
  BacklogOrganizationalUnitId,
  BacklogRequiredFilter,
  BacklogTaskCategory
} from './Backlog.types';

/**
 * A date as the legacy roster endpoints want it: a full ISO datetime.
 *
 * The employees-per-cost-centre and roster-functions calls are handled by the Groovy services, which parse
 * their dates with JsonDateTimeFormat. A date-only string is rejected there — the request fails with
 * `Unparseable date: "2026-08-17"` and a 500 — so this must not be confused with the `YYYY-MM-DD` the
 * takenlijst's own request uses. Both existing callers of those endpoints format it exactly this way.
 */
export const toLegacyApiDateTime = (date: Date): string =>
  dayjs(date).toISOString();

/**
 * The entity filters that narrow the list on screen: only those of the entity type being shown.
 *
 * The popover only offers the active view's filters, so only those may reach the backend — a pick left over
 * from another view would narrow the list with a filter the planner can neither see nor clear. The picks
 * themselves are kept, and apply again as soon as that view is chosen.
 */
export const entityFiltersForView = (
  entityFilters: BacklogEntityFilter[],
  rowEntityType: PlanningEntityType
): BacklogEntityFilter[] =>
  entityFilters.filter(
    ({ planningEntityType }) => planningEntityType === rowEntityType
  );

/**
 * The tile selection as the backend's category flags. Every category is named explicitly rather than
 * only the selected ones: an absent flag means "detect it" on the wire, so leaving one out would ask for
 * exactly the work the planner switched off.
 */
export const toCategoryFilter = (
  activeCategories: BacklogTaskCategory[]
): BacklogCategoryFilter =>
  Object.fromEntries(
    BACKLOG_CATEGORIES.map(({ category }) => [
      category,
      activeCategories.includes(category)
    ])
  ) as BacklogCategoryFilter;

/**
 * Hands the care receiver over to their own page through session storage and navigates to the
 * prescriptions tab, so a planner can see the prescription behind a deficit straight away.
 *
 * The tab travels in the hash — the Dojo host strips `&` params before matching the route — while the
 * care receiver travels in session storage: that page keeps its selection in state, and a label
 * containing a comma has no business in an unencoded hash param.
 */
export const navigateToCareReceiverPrescriptions = (
  careReceiverId: string,
  name: string
) => {
  const handover: CareReceiverHandover = { careReceiverId, name };

  window.sessionStorage.setItem(
    CARE_RECEIVERS_SESSION_STORAGE_KEYS.SELECTED_CARE_RECEIVER,
    JSON.stringify(handover)
  );

  window.location.href = `#roster,careReceivers&page=${TabType.PRESCRIPTIONS}`;
};

/**
 * Sends the planner to afsprakenplanning with this entity's agenda open, so the appointments behind a
 * task can be seen and moved where they are planned.
 *
 * The entity travels through session storage in afsprakenplanning's own {@link CalendarSourceData}
 * shape, so that page opens it with the same call its own controls use — see
 * `useOpenHandedOverCalendar`. Same reasoning as the prescriptions handover above: the Dojo host strips
 * `&` params off the hash, and a label with a comma does not belong in one unencoded.
 */
export const navigateToEntityCalendar = (
  entityId: string,
  label: string,
  planningEntityType: PlanningEntityType
) => {
  const source: CalendarSourceData = {
    label,
    planningEntityIdentifier: { entityId, planningEntityType }
  };

  window.sessionStorage.setItem(
    MRP_SESSION_STORAGE_KEYS.CALENDAR_TO_OPEN,
    JSON.stringify(source)
  );

  window.location.href = '#roster,mrp';
};

/**
 * Whether a category can describe a row of the given type.
 *
 * One predicate for both the tiles and the request, so the page cannot grey a tile out while still asking
 * the backend to detect it — which would be the detection work for rows that cannot exist.
 */
export const categoryAppliesTo = (
  category: BacklogTaskCategory,
  rowEntityType: PlanningEntityType
): boolean =>
  BACKLOG_CATEGORIES.find(
    (meta) => meta.category === category
  )?.entityTypes.includes(rowEntityType) ?? false;

/**
 * The scope the view on screen cannot work without, or null when it has one.
 *
 * Either a plan unit or the person the planner names — one of the two is enough, for both views. With
 * neither, the request resolves to nobody server-side and comes back empty; this is the guard that says
 * which field to fill instead of showing a grid that is empty for a reason nothing on it states.
 *
 * The person is read from `entityFilters` for both views: an employee pick is a planning-entity search
 * result exactly like a patient pick.
 *
 * One predicate for the query's `enabled`, the page's empty state and the popover's required marking, so
 * the page cannot end up explaining that something is missing while the request goes out anyway.
 */
export const missingRequiredFilter = ({
  rowEntityType,
  entityFilters,
  organizationalUnitIds
}: {
  rowEntityType: PlanningEntityType;
  entityFilters: BacklogEntityFilter[];
  organizationalUnitIds: BacklogOrganizationalUnitId[];
}): BacklogRequiredFilter | null => {
  if (rowEntityType !== 'CLIENT' && rowEntityType !== 'EMPLOYEE') {
    return null;
  }
  if (
    organizationalUnitIds.length > 0 ||
    entityFiltersForView(entityFilters, rowEntityType).length > 0
  ) {
    return null;
  }
  return rowEntityType === 'CLIENT'
    ? 'planUnitOrCareReceiver'
    : 'planUnitOrEmployee';
};
